import produce from "immer";

import {
  type ProgramTheme,
  type WithOrganization,
  type VerificationResponse,
  type ViewModel,
  type InactiveMilitaryPersonalInfoViewModel,
  type PersonalInfoResponse,
  type VerificationStep,
  type PersonalInfoViewModel,
  type DocUploadViewModel,
  type Locale,
  type Country,
  type FieldValidationErrors,
  type ErrorId,
  type FieldId,
} from "../types/types";
import { hooks } from "../hooks";
import { VerificationStepsEnum, isSuccessResponse } from "../types/runtimeTypes";
import { getEmptyViewModel } from "../ServerApi/VerificationRequestGetters";
import {
  MAX_DOC_UPLOAD_DOCS_ALLOWED,
  QUERY_STRING_EXTERNAL_USER_ID,
  UPLOAD_FILE_PREFIX,
} from "../../constants";
import { deepClone } from "../utils/objects";
import { getQueryOrOption } from "../ServerApi/TestingRouteOverrides";
import { initialErrorStructure } from "../types/empties";
import { getQueryParam } from "../utils/routing/Url";

/**
 * @todo needs tests
 * @private
 */
export const handleSubmitResponse = (
  verificationResponse: VerificationResponse,
  viewModel: ViewModel,
  programTheme: ProgramTheme,
): ViewModel => {
  const { currentStep } = verificationResponse;

  if (isSuccessResponse(verificationResponse)) {
    hooks.emit("ON_VERIFICATION_SUCCESS", verificationResponse);
  }

  // TODO: Find a better way to persist data between military steps.
  // Ensure the new viewModel for military steps persists data from the previous military step.
  let newViewModel: ViewModel;

  if (
    currentStep === VerificationStepsEnum.collectActiveMilitaryPersonalInfo &&
    !programTheme.transitionalMilitaryValidityDays
  ) {
    newViewModel = { ...viewModel, dischargeDate: "" } as InactiveMilitaryPersonalInfoViewModel;
    delete (newViewModel as InactiveMilitaryPersonalInfoViewModel).dischargeDate;
  } else if (currentStep === VerificationStepsEnum.collectInactiveMilitaryPersonalInfo) {
    if (!(viewModel as InactiveMilitaryPersonalInfoViewModel).dischargeDate) {
      newViewModel = { ...viewModel, dischargeDate: "" } as InactiveMilitaryPersonalInfoViewModel;
    } else {
      newViewModel = viewModel;
    }
  } else if (hasFailedInstantMatch(verificationResponse as PersonalInfoResponse)) {
    newViewModel = viewModel;
  } else {
    // If not military, and not on the confirm info 'step', get an empty viewModel
    const { locale } = verificationResponse;
    newViewModel = initViewModel({
      locale,
      previousViewModel: viewModel,
      currentStep,
      externalUserId: getQueryOrOption(QUERY_STRING_EXTERNAL_USER_ID),
    });
  }
  return newViewModel;
};

/**
 * @private
 */
export const hasFailedInstantMatch = (verificationResponse: PersonalInfoResponse): boolean => {
  if (verificationResponse.instantMatchAttempts && verificationResponse.instantMatchAttempts > 0) {
    return true;
  }
  return false;
};

/**
 * @private
 */
export const initViewModel = ({
  previousViewModel,
  currentStep,
  locale,
  fingerprint,
  country,
  externalUserId,
}: {
  previousViewModel: ViewModel;
  currentStep: VerificationStep;
  locale: Locale;
  fingerprint?: string;
  country?: Country;
  externalUserId?: string;
}): ViewModel => {
  const viewModel = getEmptyViewModel(currentStep);

  // Attempt to bring over any previousViewModel values that apply to the new viewModel
  if (previousViewModel) {
    Object.getOwnPropertyNames(viewModel).forEach((key) => {
      if (previousViewModel.hasOwnProperty(key)) {
        viewModel[key] = previousViewModel[key];
      }
    });
  }

  if (viewModel) {
    if (fingerprint) {
      (viewModel as PersonalInfoViewModel).deviceFingerprintHash = fingerprint;
    }
    if (locale) {
      (viewModel as PersonalInfoViewModel).localeChoice = {
        value: locale || "en-US",
        label: "",
      };
    }
    if (country && "country" in viewModel && viewModel.country === undefined) {
      viewModel.country = country;
    }
    if (country && "countryChoice" in viewModel && viewModel.countryChoice === undefined) {
      viewModel.countryChoice = {
        value: country,
        label: "",
      };
    }
    if (externalUserId) {
      (viewModel as PersonalInfoViewModel).externalUserId = externalUserId;
    }
  }

  return viewModel;
};

export const clearUploadedFiles = (
  viewModel: DocUploadViewModel,
): DocUploadViewModel | undefined => {
  if (viewModel) {
    return produce(viewModel, (draft) => {
      for (let i = 1; i <= MAX_DOC_UPLOAD_DOCS_ALLOWED; i += 1) {
        draft[`${UPLOAD_FILE_PREFIX}${i}`] = undefined;
      }
    });
  }
  return undefined;
};

export const collectThreatMetrixProfile = async (verificationId: string, programId: string) => {
  // Clean up: DS-2040
  const programIdToOrgId: Record<string, string> = {
    "63a35d55add1107a3bc5edb6": "4a6mj7xe",
    "60870a785f8f9c61aca0f11f": "4a6mj7xe",
    "63fd266996552d469aea40e1": "4a6mj7xe",
    "686eaf99d55e893b64da72b5": "41vh0yph",
    "6835da88629e1c44b45c049b": "41vh0yph",
    "5e94c9ee0ec57b1ba907a4fa": "41vh0yph",
    "5d83fe3def70971fc2f6b3c7": "41vh0yph",
    "5be5cce4895f11144700bf66": "41vh0yph",
    "68af74c746f35c0c33b11f7e": "41vh0yph",
    "5b609e64944963167d6cde7b": "41vh0yph",
    "68371bf424e7d8407ba61551": "41vh0yph",
    "5f3d73325c81e518c080bf01": "41vh0yph",
    "5bfed239e69819126c9e2300": "6s33lpne",
    "5d9e307c45b7b120b75cdc7a": "6s33lpne",
    "633f45d7295c0551ab43b87a": "6s33lpne",
    "6283e0f3401a404cb53704e3": "6s33lpne",
    "5bfed238932f4b1330576e4d": "6s33lpne",
    "61ccc40cbfb19932e5e7f220": "41vh0yph",
    "6058e5f822ca3400652e304b": "41vh0yph",
    "674de64fa3df0f5354b4cd93": "41vh0yph",
    "6008b2d67ea692195efe4047": "41vh0yph",
    "6008b36aa65a69195e48bef9": "41vh0yph",
    "6019d9fbc66edf1a0fd19b8b": "41vh0yph",
    "6054c8d3bbcc170065c88b37": "41vh0yph",
    "6054c8bdbbcc170065c8892c": "41vh0yph",
    "61ca3c1db1fdc21390a239b3": "38grtc73",
    "69173080e7d00d521ce139c0": "38grtc73",
    "622276a7c2d4683557eedb56": "38grtc73",
    "616626fbfe36cb56e69984e3": "38grtc73",
    "69252575f84c722495040e20": "38grtc73",
    "67d8c978ca73cd576d8c35f3": "6s33lpne",
    "6818c2f8521eb235469c3058": "6s33lpne",
    "6706daa505575b2c7476b195": "6s33lpne",
    "65494402a9f6da45f37f1a83": "6s33lpne",
    "654d269923374a743123f178": "6s33lpne",
    "644ab175f5c88f24d4850dc7": "6s33lpne",
    "64832e9d51d7ee3f0a813a19": "6s33lpne",
    "68014714e9c4e6582cf82011": "6gepep58",
    "680145d9e9c4e6582cf8126b": "6gepep58",
    "68014636e9c4e6582cf816ed": "6gepep58",
    "6879467f9a02bf76c7d1bf7a": "6gepep58",
    "68014601e9c4e6582cf813ef": "6gepep58",
    "68014598e9c4e6582cf80f5d": "6gepep58",
    "67b67bb3855c8b2c02969a4e": "6s33lpne",
    "67a3f0217c740a497f6b4af3": "6s33lpne",
    "691e317dedbe840cda54c958": "6s33lpne",
    "67c8c14f5f17a83b745e3f82": "6s33lpne",
    "690415d58971e73ca187d8c9": "bzi5zrdy",
    "67eb16281bc6f4327a3e8364": "bzi5zrdy",
    "68f7b326d3394435ecf77cab": "bzi5zrdy",
    "68d47554aa292d20b9bec8f7": "bzi5zrdy",
    "69b1aafc617d5165c65f8173": "bzi5zrdy",
    "5f174ed70eb21b1afb409e6f": "5f167ogy",
    "5914ca90e4b0145a6ef34c52": "3rsf9rwa",
    "5914ca90e4b06f86ee1e0d90": "3rsf9rwa",
    "5956bef1e4b078c2588a4632": "5f167ogy",
    "5e31c74f2a7fdb1bc72bd4ab": "5f167ogy",
    "668dabdc0c1926096b95c8a1": "5f167ogy",
    "6913c22edff1fa39908ed9a8": "5f167ogy",
    "5d8259138138dc20cc4df647": "5f167ogy",
    "5d825a6733c6c8212f737fac": "5f167ogy",
    "5e5cbd0ad4d28a1bf68bd11a": "5f167ogy",
    "5dbfc7c3caa66122c4090f7d": "5f167ogy",
    "5dbfdac94906ca2225ee1b11": "5f167ogy",
    "6205406d744e5c3ad472f2de": "5f167ogy",
    "6205418e744e5c3ad47305f6": "5f167ogy",
    "64ae259cb9a6fe2509bce6a6": "5f167ogy",
    "64aec7b31268066d5f4c8f0d": "5f167ogy",
    "64e79d7fcc49a003b0f0b3d0": "5f167ogy",
    "650443767e1254500a167ae1": "5f167ogy",
    "654938172562d9596a8f3c84": "5f167ogy",
    "5e6036e01bbe362b78749213": "5f167ogy",
    "5e6036f91bbe362b7874930e": "5f167ogy",
    "5e6036f91bbe362b7874930f": "5f167ogy",
    "5e6036fa1bbe362b78749319": "5f167ogy",
    "5e9755ed3fe1ea1b498dcd9e": "5f167ogy",
    "68274df17bc83f307fa695eb": "3rsf9rwa",
    "68372bcfc97535680d8eb83f": "3rsf9rwa",
    "683f4a14362c803b5d980d26": "3rsf9rwa",
    "68407bcf84a7f75be02f9973": "3rsf9rwa",
    "68407d0584a7f75be02fa675": "3rsf9rwa",
    "686e8ba4c4cbd93035d66b77": "3rsf9rwa",
    "6891135d1fa006329c1dcc20": "3rsf9rwa",
    "68a4950190e0fc37aa8d9c99": "3rsf9rwa",
    "68a4962590e0fc37aa8db4fe": "3rsf9rwa",
    "68a4968f90e0fc37aa8dbd05": "3rsf9rwa",
    "68a496ba90e0fc37aa8dc116": "3rsf9rwa",
    "68c858c3b7f2f75bb64a1171": "3rsf9rwa",
    "68cc2d22a8888701befa492e": "3rsf9rwa",
    "68d2e0a527c78870949877e4": "3rsf9rwa",
    "68d2e3d227c788709498be2e": "3rsf9rwa",
    "68d2e58b27c788709498e3fc": "3rsf9rwa",
    "68d2e67027c788709498f788": "3rsf9rwa",
    "68d2ebeb751f1d0ef73897ca": "3rsf9rwa",
    "68d2ecca751f1d0ef738a814": "3rsf9rwa",
    "68d2ed9f751f1d0ef738baed": "3rsf9rwa",
    "68d2ee64751f1d0ef738c94f": "3rsf9rwa",
    "68d2ef20751f1d0ef738d993": "3rsf9rwa",
    "68d2efc5751f1d0ef738e871": "3rsf9rwa",
    "68d2f0be751f1d0ef738fc49": "3rsf9rwa",
    "68f917caf6b1680a8fd3d0ee": "3rsf9rwa",
    "68ffc8d352a34f4a0e2cbf62": "3rsf9rwa",
    "69002e242e36f765828f7014": "3rsf9rwa",
    "690182953ee2411b4426f56d": "3rsf9rwa",
    "6901837d3ee2411b4426fec9": "3rsf9rwa",
    "690183f53ee2411b44270411": "3rsf9rwa",
    "6901845d3ee2411b442708fc": "3rsf9rwa",
    "690184df3ee2411b44270e85": "3rsf9rwa",
    "690185923ee2411b442715fc": "3rsf9rwa",
    "690186093ee2411b44271b63": "3rsf9rwa",
    "690186813ee2411b44272146": "3rsf9rwa",
    "690186e13ee2411b442725de": "3rsf9rwa",
    "690187703ee2411b44272c04": "3rsf9rwa",
    "690187d03ee2411b44273132": "3rsf9rwa",
    "690188353ee2411b44273532": "3rsf9rwa",
    "6902cef12b98a01a22a29e91": "3rsf9rwa",
    "6902cf822b98a01a22a2a4e6": "3rsf9rwa",
    "6902cff72b98a01a22a2a95d": "3rsf9rwa",
    "6902d0782b98a01a22a2ae95": "3rsf9rwa",
    "6902d0e72b98a01a22a2b34d": "3rsf9rwa",
    "6902d1462b98a01a22a2b6f0": "3rsf9rwa",
    "6902d1af2b98a01a22a2bb04": "3rsf9rwa",
    "6902d20f2b98a01a22a2be7c": "3rsf9rwa",
    "6902d2762b98a01a22a2c1e6": "3rsf9rwa",
    "6902d2e52b98a01a22a2c6e9": "3rsf9rwa",
    "6902d34f2b98a01a22a2cb59": "3rsf9rwa",
    "6902d3cd2b98a01a22a2d135": "3rsf9rwa",
    "690426732b98a01a22b3c033": "3rsf9rwa",
    "6904278b2b98a01a22b3cb4a": "3rsf9rwa",
    "690427f02b98a01a22b3cef9": "3rsf9rwa",
    "6904286a2b98a01a22b3d40d": "3rsf9rwa",
    "6904295d2b98a01a22b3ddb4": "3rsf9rwa",
    "690429d92b98a01a22b3e2bd": "3rsf9rwa",
    "69042a912b98a01a22b3ea4a": "3rsf9rwa",
    "69042af92b98a01a22b3eea7": "3rsf9rwa",
    "69042b802b98a01a22b3f3b6": "3rsf9rwa",
    "69042beb2b98a01a22b3f80c": "3rsf9rwa",
    "690bafe03485ec5ffc5231bc": "3rsf9rwa",
    "690ceefb73ab732b3d611232": "3rsf9rwa",
    "6917e0d312bb1416c9066551": "3rsf9rwa",
    "692090dcdd45593ee750a8cb": "3rsf9rwa",
    "6941c20d2f38af186f9c0429": "3rsf9rwa",
    "6941c3272f38af186f9c1034": "3rsf9rwa",
    "6941c46f2f38af186f9c1dd4": "3rsf9rwa",
    "6941c5192f38af186f9c25c4": "3rsf9rwa",
    "6941c5912f38af186f9c2b89": "3rsf9rwa",
    "6941c61b2f38af186f9c3237": "3rsf9rwa",
    "6941c6c82f38af186f9c39d6": "3rsf9rwa",
    "6941c72b2f38af186f9c3e05": "3rsf9rwa",
    "6941c79b2f38af186f9c42a2": "3rsf9rwa",
    "6941c86b2f38af186f9c4c3c": "3rsf9rwa",
    "69611d0acadd212961128d5e": "3rsf9rwa",
    "6966aad6084861118d9059af": "3rsf9rwa",
    "699764e95061d450695a8ce2": "5f167ogy",
    "69a8b67049b85d7b53b4cadb": "5f167ogy",
  };

  let orgId = programIdToOrgId[programId];

  if (!orgId) {
    orgId = "cnl2my47";
  }

  import(/* webpackChunkName: "Tmetrix" */ "../ThreatMetrix/index")
    .then(({ ThreatMetrix }) => {
      if (
        ThreatMetrix !== undefined &&
        ThreatMetrix.hasOwnProperty("profile") &&
        typeof ThreatMetrix.profile === "function"
      ) {
        const profilingDomain = "content.sheerid.com";
        ThreatMetrix.profile(profilingDomain, orgId, verificationId);
      }
    })
    .catch(() => {
      // Do nothing - API call will handle this
    });
};

export const enhanceOrganizationFromTheme = (viewModel: ViewModel, programTheme: ProgramTheme) => {
  if (
    viewModel &&
    viewModel.hasOwnProperty("organization") &&
    programTheme.config.orgRemoteSource
  ) {
    const cloned: ViewModel = deepClone(viewModel);
    const { organization } = cloned as WithOrganization;
    organization.source = programTheme.config.orgRemoteSource;
    return cloned;
  }
  return viewModel;
};

export const determineCountry = (
  verificationResponse: VerificationResponse,
  countries: Country[],
): Country => {
  if (verificationResponse?.country) return verificationResponse.country;

  const defaultCountry = "US";
  if (!Array.isArray(countries) || countries.length === 0) return defaultCountry;
  if (countries.length === 1) return countries[0];

  const countryQueryParam = getQueryParam("country")?.toUpperCase();
  return countries.find((c) => c === countryQueryParam);
};

export const fieldIdErrorMap = new Map<ErrorId, FieldId>([
  ["invalidFirstName", "firstName"],
  ["invalidLastName", "lastName"],
  ["invalidMemberId", "memberId"],
  ["invalidEmail", "email"],
  ["invalidPhoneNumber", "phoneNumber"],
  ["invalidBirthDate", "birthDate"],
  ["invalidOrganization", "organization"],
  ["invalidPostalCode", "postalCode"],
  ["invalidAddress", "address1"],
  ["invalidCity", "city"],
  ["invalidCountry", "country"],
  ["invalidState", "state"],
  ["invalidActiveDutyStartDate", "activeDutyStartDate"],
  ["invalidDischargeDate", "dischargeDate"],
  ["invalidStatus", "status"],
  ["invalidStatuses", "statuses"],
  ["invalidSocialSecurityNumber", "socialSecurityNumber"],
  ["invalidDriverLicenseNumber", "driverLicenseNumber"],
  ["invalidEbtCardNumber", "ebtCardNumber"],
  ["invalidOverrideCode", "overrideCode"],
  ["invalidCvecNumber", "cvecNumber"],
  ["invalidCaptchaToken", "captchaToken"],
]);

export const getServerFieldValidationErrors = (
  serverErrorIds: ErrorId[],
): FieldValidationErrors => {
  let fieldValidationErrors = deepClone(initialErrorStructure);

  serverErrorIds.forEach((serverErrorId: ErrorId) => {
    const fieldId = fieldIdErrorMap.get(serverErrorId);
    if (fieldId) {
      fieldValidationErrors = { ...fieldValidationErrors, [fieldId]: serverErrorId };
    }
  });

  return fieldValidationErrors;
};
